// Membership widgets: browse a venue's recurring membership plans, then continue to 365 to sign in + pay.
// Two mounts share this module:
//   data-365="membership"          → grid of pricing cards (with "what's included" bullets)
//   data-365="membership-compare"  → side-by-side comparison table
// Browse data is anonymous. The transactional step (login + Stripe subscription + confirmation) happens
// FIRST-PARTY on the 365 platform: clicking Join redirects the whole page to {web}/embed-membership with
// the chosen plan, and the platform returns the customer here with ?membership=success. No platform fee.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, money, renderError, renderLoading, footer, clear, signOutRow, stripQueryParam, showSalesPaused, blockIfSalesPaused, taxFor, totalWithTax, taxNoteEl } from '../ui';
import { sitePage, hasMount } from '../pages';
import { track, withWa } from '../analytics';

interface MembershipPlan {
  id: string;
  name: string;
  description?: string | null;
  priceMonthly?: number | null;
  priceAnnual?: number | null;
  features?: string | null;
  /** The venue's cancellation notice period in days (0/absent = none). Monthly billing only. */
  cancellationNoticeDays?: number | null;
  /** Fallback for EmbedConfig.salesTaxPercent (absent on older API builds). */
  salesTaxPercent?: number | null;
}

function noticeLabel(p: MembershipPlan): string {
  const d = p.cancellationNoticeDays ?? 0;
  return d > 0 ? `${d} days’ notice to cancel` : '';
}

type Interval = 'Monthly' | 'Annual';
const per = (iv: Interval) => (iv === 'Annual' ? '/yr' : '/mo');

/** The sales-tax rate to quote: the embed config's, else the plan's own, else 0 (nothing shown). */
function taxPct(cfg: EmbedConfig, p?: MembershipPlan | null): number {
  const v = cfg.salesTaxPercent ?? p?.salesTaxPercent ?? 0;
  return Number(v) > 0 ? Number(v) : 0;
}

/** "$40.00/mo" or, when the venue taxes, "$40.00/mo + tax" — the list price on a card / table cell. */
function listPrice(price: number, iv: Interval, pct: number): string {
  return `${money(price)}${per(iv)}${taxFor(price, pct) > 0 ? ' + tax' : ''}`;
}

/** "$40.00 + $3.30 tax = $43.30/mo" — the join step's breakdown; '' when there is no tax. */
function taxBreakdown(price: number, iv: Interval, pct: number): string {
  const t = taxFor(price, pct);
  return t > 0 ? `${money(price)} + ${money(t)} tax = ${money(totalWithTax(price, pct))}${per(iv)}` : '';
}

/** The Join action names the REAL recurring charge: "Join — $43.30/mo" (plain "Join" when untaxed). */
function joinLabel(price: number | null | undefined, iv: Interval, pct: number): string {
  return price != null && taxFor(price, pct) > 0 ? `Join — ${money(totalWithTax(price, pct))}${per(iv)}` : 'Join';
}

function platformBase(cfg: EmbedConfig): string {
  return (cfg.loginUrl || '').replace(/\/embed-login\.html.*$/, '').replace(/\/$/, '');
}

/** "What's included" benefits are stored newline-delimited; split into trimmed, non-empty lines. */
function parseFeatures(features?: string | null): string[] {
  if (!features) return [];
  return features.split('\n').map((s) => s.trim()).filter(Boolean);
}

function fetchPlans(cfg: EmbedConfig, api: Api): Promise<MembershipPlan[]> {
  return api.get<MembershipPlan[]>(`/api/memberships/public/${cfg.venueId}/plans`);
}

/** Redirect the page to the first-party 365 join flow for the chosen plan + interval. */
function startJoin(cfg: EmbedConfig, p: MembershipPlan, interval: 'Monthly' | 'Annual', btn: HTMLButtonElement): void {
  if (blockIfSalesPaused(cfg, btn)) return;
  const price = interval === 'Annual' ? p.priceAnnual : p.priceMonthly;
  const params = new URLSearchParams({
    venue: cfg.venueId,
    plan: p.id,
    planName: p.name,
    interval,
    return: location.href.split('?')[0], // strip any prior ?membership=success
  });
  if (price != null) params.set('price', String(price));
  btn.disabled = true;
  btn.textContent = 'Continuing…';
  track('membership_start', 'membership');
  window.location.href = withWa(`${platformBase(cfg)}/embed-membership?${params.toString()}`);
}

// ============================================================================
// Cards widget — data-365="membership"
// ============================================================================

export function mountMembership(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  showSalesPaused(root, cfg);
  void load(root, cfg, api, auth);
}

/**
 * Plan ids covered by the signed-in user's Active/PastDue memberships at THIS venue. Empty when
 * logged out or on any error — Join then renders as before and the server still rejects double-joins.
 * (Matches the server's duplicate guard: Incomplete/Cancelled don't block a join, so they don't count.)
 */
async function fetchOwnedPlanIds(cfg: EmbedConfig, api: Api): Promise<Set<string>> {
  try {
    const all = await api.get<MyMembership[]>('/api/memberships/me');
    const venueId = (cfg.venueId || '').toLowerCase();
    return new Set((all || [])
      .filter((m) => (m.venueId || '').toLowerCase() === venueId
        && (m.status === 'Active' || m.status === 'PastDue') && m.membershipPlanId)
      .map((m) => (m.membershipPlanId || '').toLowerCase()));
  } catch {
    return new Set();
  }
}

async function load(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  if (!cfg.membershipsEnabled) {
    renderError(root, 'Memberships aren’t available for this venue right now.');
    return;
  }
  renderLoading(root, 'Memberships');
  let plans: MembershipPlan[];
  let owned = new Set<string>();
  try {
    plans = await fetchPlans(cfg, api);
    if (auth.isLoggedIn) owned = await fetchOwnedPlanIds(cfg, api);
  } catch {
    renderError(root, 'We couldn’t load memberships. Please try again.', () => void load(root, cfg, api, auth));
    return;
  }
  renderPlans(root, cfg, plans, owned);
}

function renderPlans(root: HTMLElement, cfg: EmbedConfig, plans: MembershipPlan[], owned: Set<string>): void {
  clear(root);
  const joined = new URLSearchParams(location.search).get('membership') === 'success';
  if (joined) track('membership_success', 'membership'); // funnel: before the flag is stripped
  if (joined) stripQueryParam('membership'); // don't replay the banner on refresh
  const children: (HTMLElement | string | null)[] = [h('h3', undefined, 'Memberships')];
  if (joined) {
    children.push(h('div', { class: 'e365-alert info' },
      '✓ You’re a member! Your member card (with check-in QR) is on this page — and in your 365 account.'));
  }
  if (!plans || plans.length === 0) {
    children.push(h('div', { class: 'e365-alert info' }, 'No membership plans are available yet.'), footer());
    root.append(...children.filter((c): c is HTMLElement | string => c != null));
    return;
  }
  const grid = h('div', { class: 'e365-plan-grid' });
  for (const p of plans) grid.appendChild(renderPlanCard(cfg, p, owned.has(p.id.toLowerCase())));
  const taxLine = taxNoteEl(taxPct(cfg, plans[0]));
  if (taxLine) taxLine.style.marginTop = '10px';
  children.push(grid,
    taxLine, // "Prices plus 8.25% sales tax" — null (nothing) when the venue doesn't tax
    h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:10px' },
      'You’ll sign in to your 365 account to join. Cancel anytime.'),
    footer());
  root.append(...children.filter((c): c is HTMLElement | string => c != null));
}

function renderPlanCard(cfg: EmbedConfig, p: MembershipPlan, isMember: boolean): HTMLElement {
  const hasMonthly = p.priceMonthly != null && p.priceMonthly > 0;
  const hasAnnual = p.priceAnnual != null && p.priceAnnual > 0;
  let interval: Interval = hasMonthly ? 'Monthly' : 'Annual';
  const pct = taxPct(cfg, p);

  const priceEl = h('div', { class: 'e365-plan-price' });
  // "$40.00 + $3.30 tax = $43.30/mo" under the list price — the join step's real charge, read here.
  const breakdownEl = h('div', { class: 'e365-muted', style: 'display:none; font-size:.78rem; margin:-4px 0 8px' });
  const joinBtn = h('button', { class: 'e365-btn' }, 'Join') as HTMLButtonElement;

  const refresh = () => {
    const price = interval === 'Annual' ? p.priceAnnual : p.priceMonthly;
    clear(priceEl);
    if (price != null) {
      priceEl.append(
        h('span', { class: 'e365-plan-amt' }, money(price)),
        h('span', { class: 'e365-plan-per' }, per(interval) + (taxFor(price, pct) > 0 ? ' + tax' : '')),
      );
    }
    const bd = price != null ? taxBreakdown(price, interval, pct) : '';
    breakdownEl.textContent = bd;
    breakdownEl.style.display = bd ? '' : 'none';
    joinBtn.textContent = joinLabel(price, interval, pct);
  };

  const toggle = h('div', { class: 'e365-slots' });
  if (hasMonthly && hasAnnual) {
    const mBtn = h('button', { class: 'e365-slot', type: 'button' }, 'Monthly') as HTMLButtonElement;
    const aBtn = h('button', { class: 'e365-slot', type: 'button' }, 'Annual') as HTMLButtonElement;
    const sync = () => {
      mBtn.setAttribute('aria-pressed', String(interval === 'Monthly'));
      aBtn.setAttribute('aria-pressed', String(interval === 'Annual'));
      refresh();
    };
    mBtn.addEventListener('click', () => { interval = 'Monthly'; sync(); });
    aBtn.addEventListener('click', () => { interval = 'Annual'; sync(); });
    toggle.append(mBtn, aBtn);
    interval = 'Monthly';
    sync();
  } else {
    refresh();
  }

  joinBtn.addEventListener('click', () => startJoin(cfg, p, interval, joinBtn));

  const card = h('div', { class: 'e365-plan' }, h('div', { class: 'e365-plan-name' }, p.name));
  if (p.description) card.appendChild(h('div', { class: 'e365-plan-desc' }, p.description));
  const featUl = h('ul', { class: 'e365-plan-feat' }); // empty list still acts as the flex spacer (keeps prices aligned)
  for (const f of parseFeatures(p.features)) featUl.appendChild(h('li', undefined, f));
  card.appendChild(featUl);
  if (isMember) {
    // Already subscribed (Active/PastDue) — no Join (the server would reject a second one anyway).
    card.appendChild(priceEl);
    card.appendChild(h('div', {
      style: 'text-align:center; font-weight:700; color:var(--e365-primary); padding:9px 0; border:1px solid var(--e365-border); border-radius:var(--e365-radius);',
    }, '✓ You’re a member'));
    return card;
  }
  if (hasMonthly && hasAnnual) card.appendChild(toggle);
  card.appendChild(priceEl);
  card.appendChild(breakdownEl);
  // The venue's notice period, read BEFORE joining so the "charged once more" rule is never a surprise.
  const notice = noticeLabel(p);
  if (notice) card.appendChild(h('div', { class: 'e365-muted', style: 'font-size:.78rem; margin:-4px 0 8px' }, notice));
  card.appendChild(joinBtn);
  return card;
}

// ============================================================================
// Compare widget — data-365="membership-compare"
// ============================================================================

export function mountMembershipCompare(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  showSalesPaused(root, cfg);
  void loadCompare(root, cfg, api, auth);
}

async function loadCompare(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  if (!cfg.membershipsEnabled) {
    renderError(root, 'Memberships aren’t available for this venue right now.');
    return;
  }
  renderLoading(root, 'Compare memberships');
  let plans: MembershipPlan[];
  let owned = new Set<string>();
  try {
    plans = await fetchPlans(cfg, api);
    if (auth.isLoggedIn) owned = await fetchOwnedPlanIds(cfg, api);
  } catch {
    renderError(root, 'We couldn’t load memberships. Please try again.', () => void loadCompare(root, cfg, api, auth));
    return;
  }
  renderCompare(root, cfg, plans, owned);
}

function renderCompare(root: HTMLElement, cfg: EmbedConfig, plans: MembershipPlan[], owned: Set<string>): void {
  clear(root);
  const children: (HTMLElement | string | null)[] = [h('h3', undefined, 'Compare memberships')];
  if (!plans || plans.length === 0) {
    children.push(h('div', { class: 'e365-alert info' }, 'No membership plans are available yet.'), footer());
    root.append(...children.filter((c): c is HTMLElement | string => c != null));
    return;
  }

  const dash = (): HTMLElement => h('span', { class: 'e365-muted' }, '—');
  const tr = (label: string, cells: (HTMLElement | string)[]): HTMLElement =>
    h('tr', undefined, h('th', { scope: 'row' }, label), ...cells.map((c) => h('td', undefined, c)));

  const head = h('tr', undefined, h('th', undefined, ''), ...plans.map((p) => h('th', undefined, p.name)));
  const rows: HTMLElement[] = [];

  if (plans.some((p) => p.priceMonthly != null))
    rows.push(tr('Monthly', plans.map((p) => p.priceMonthly != null
      ? h('span', { class: 'e365-cmp-amt' }, listPrice(p.priceMonthly, 'Monthly', taxPct(cfg, p))) : dash())));

  if (plans.some((p) => p.priceAnnual != null))
    rows.push(tr('Annual', plans.map((p) => p.priceAnnual != null
      ? h('span', { class: 'e365-cmp-amt' }, listPrice(p.priceAnnual, 'Annual', taxPct(cfg, p))) : dash())));

  if (plans.some((p) => (p.cancellationNoticeDays ?? 0) > 0))
    rows.push(tr('Cancelling', plans.map((p) => noticeLabel(p) ? h('span', undefined, noticeLabel(p)) : dash())));

  if (plans.some((p) => p.priceMonthly != null && p.priceAnnual != null))
    rows.push(tr('Save annually', plans.map((p) => {
      if (p.priceMonthly != null && p.priceAnnual != null) {
        const s = p.priceMonthly * 12 - p.priceAnnual;
        return s > 0 ? h('span', { class: 'e365-cmp-save' }, `${money(s)}/yr`) : dash();
      }
      return dash();
    })));

  if (plans.some((p) => parseFeatures(p.features).length > 0))
    rows.push(tr("What's included", plans.map((p) => {
      const items = parseFeatures(p.features);
      if (!items.length) return dash();
      const ul = h('ul', { class: 'e365-cmp-feat' });
      for (const f of items) ul.appendChild(h('li', undefined, f));
      return ul;
    })));

  rows.push(tr('', plans.map((p) => {
    if (owned.has(p.id.toLowerCase()))
      return h('span', { style: 'font-weight:700; color:var(--e365-primary); white-space:nowrap;' }, '✓ You’re a member');
    const interval: Interval = (p.priceMonthly != null && p.priceMonthly > 0) ? 'Monthly' : 'Annual';
    const price = interval === 'Annual' ? p.priceAnnual : p.priceMonthly;
    const pct = taxPct(cfg, p);
    // The Join cell = the real recurring charge, with its breakdown above the button when taxed.
    const cell = h('div');
    const bd = price != null ? taxBreakdown(price, interval, pct) : '';
    if (bd) cell.append(h('div', { class: 'e365-muted', style: 'font-size:.76rem; margin-bottom:2px' }, bd));
    const btn = h('button', { class: 'e365-btn' }, joinLabel(price, interval, pct)) as HTMLButtonElement;
    btn.addEventListener('click', () => startJoin(cfg, p, interval, btn));
    cell.append(btn);
    return cell;
  })));

  const table = h('table', { class: 'e365-cmp' }, h('thead', undefined, head), h('tbody', undefined, ...rows));
  const taxLine = taxNoteEl(taxPct(cfg, plans[0]));
  if (taxLine) taxLine.style.marginTop = '10px';
  children.push(h('div', { class: 'e365-cmp-wrap' }, table),
    taxLine, // "Prices plus 8.25% sales tax" — null (nothing) when the venue doesn't tax
    h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:10px' },
      'You’ll sign in to your 365 account to join. Cancel anytime.'),
    footer());
  root.append(...children.filter((c): c is HTMLElement | string => c != null));
}

// ============================================================================
// Manage widget — data-365="my-membership"
// The signed-in member's subscription for THIS venue: status, renew/cancel date, plus
// Cancel (at period end) and Update card (Stripe Billing Portal on the venue's connected account).
// Login-gated read (same model as member-card); actions are authenticated POSTs.
// ============================================================================

interface MyMembership {
  id: string;
  venueId?: string | null;
  membershipPlanId?: string | null;  // current plan (to mark it in the change-plan picker)
  planName?: string | null;
  status?: string | null;            // Active / PastDue / Cancelled / Incomplete
  interval?: string | null;          // Monthly / Annual
  price?: number | null;             // PRE-tax recurring price
  /** Sales tax on each renewal, when the API reports it (not on older builds → "+ tax" wording). */
  taxAmount?: number | null;
  currentPeriodEndUtc?: string | null;
  cancelledAtUtc?: string | null;    // set → cancel-at-period-end pending
  planChangeAvailableUtc?: string | null; // future → already changed this period (cooldown)
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function mountMyMembership(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'my-membership') auth.clearPending();
  void renderManage(root, cfg, api, auth);
}

async function renderManage(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'My Membership'));

  if (!cfg.membershipsEnabled) {
    root.append(h('div', { class: 'e365-alert info' }, 'Memberships aren’t available for this venue right now.'), footer());
    return;
  }

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to view and manage your membership.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-membership' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'My Membership');

  let all: MyMembership[];
  try {
    all = await api.get<MyMembership[]>('/api/memberships/me');
  } catch (e) {
    clear(host);
    if (e instanceof AuthRequiredError) {
      host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Please sign in again to view your membership.'));
      const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
      login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-membership' }));
      host.append(login, footer());
    } else {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your membership. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void renderManage(root, cfg, api, auth));
      host.append(retry, footer());
    }
    return;
  }

  const venueId = (cfg.venueId || '').toLowerCase();
  const mine = (all || []).filter((m) => (m.venueId || '').toLowerCase() === venueId);

  clear(host);
  if (mine.length === 0) {
    host.append(
      hasMount('membership')
        ? h('div', { class: 'e365-alert info' }, 'You’re not a member here yet. Join a plan on this page to get started.')
        : h('div', { class: 'e365-alert info' }, 'You’re not a member here yet. ',
            h('a', { href: sitePage('membership') }, 'See the membership plans'), ' to get started.'),
      signOutRow(auth.profile?.email, () => { auth.logout(); void renderManage(root, cfg, api, auth); }),
      footer(),
    );
    return;
  }

  for (const m of mine) host.append(renderManageCard(m, mine, cfg, api, auth, root));
  host.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void renderManage(root, cfg, api, auth); }),
    footer(),
  );
}

function statusBadge(status?: string | null): HTMLElement {
  const s = status || '';
  const bg: Record<string, string> = {
    Active: '#0a7d33', PastDue: '#b23b00', Cancelled: 'var(--e365-muted)', Incomplete: 'var(--e365-muted)',
  };
  const label = s === 'PastDue' ? 'Past due' : (s || 'Unknown');
  return h('span', {
    style: `display:inline-block; font-size:.72rem; font-weight:700; padding:2px 8px; border-radius:999px; color:#fff; background:${bg[s] || 'var(--e365-muted)'};`,
  }, label);
}

function renderManageCard(m: MyMembership, mine: MyMembership[], cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement): HTMLElement {
  const card = h('div', {
    style: 'border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:14px; margin-bottom:10px; background:var(--e365-surface);',
  });

  card.append(h('div', { style: 'display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:4px;' },
    h('div', { style: 'font-weight:700; font-size:1.05rem;' }, m.planName || 'Membership'),
    statusBadge(m.status)));

  if (m.price != null) {
    const iv: Interval = m.interval === 'Annual' ? 'Annual' : 'Monthly';
    const tax = Number(m.taxAmount) || 0;
    // Exact when the API reports the tax it charges ("$43.30/mo (incl. $3.30 sales tax)"); otherwise
    // the list price "+ tax" when the venue taxes — never a number this widget only guessed at.
    const line = tax > 0
      ? `${money(Math.round((m.price + tax) * 100) / 100)}${per(iv)} (incl. ${money(tax)} sales tax)`
      : listPrice(m.price, iv, taxPct(cfg));
    card.append(h('div', { class: 'e365-muted', style: 'font-size:.85rem;' }, line));
  }

  const cancelPending = !!m.cancelledAtUtc;
  const end = fmtDate(m.currentPeriodEndUtc);
  if (m.status === 'PastDue') {
    card.append(h('div', { class: 'e365-alert error', style: 'margin-top:8px;' },
      'Your last payment didn’t go through. Update your card to keep your membership active.'));
  } else if (cancelPending && end) {
    card.append(h('div', { class: 'e365-alert info', style: 'margin-top:8px;' }, `Cancelled — access continues until ${end}, then it won’t renew.`));
  } else if (m.status === 'Active' && end) {
    card.append(h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin-top:4px;' }, `Renews on ${end}`));
  } else if (m.status === 'Cancelled') {
    card.append(hasMount('membership')
      ? h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin-top:4px;' }, 'This membership has ended. Join a plan on this page to rejoin.')
      : h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin-top:4px;' }, 'This membership has ended. ',
          h('a', { href: sitePage('membership') }, 'See the plans'), ' to rejoin.'));
  }

  const actions = h('div', { class: 'e365-slots', style: 'margin-top:10px;' });
  if (m.status === 'Active' || m.status === 'PastDue') {
    const updateBtn = h('button', { class: 'e365-btn', type: 'button' }, 'Update card') as HTMLButtonElement;
    updateBtn.addEventListener('click', () => void openBillingPortal(m, api, updateBtn, auth));
    actions.append(updateBtn);
  }
  const cd = m.planChangeAvailableUtc ? new Date(m.planChangeAvailableUtc) : null;
  const inCooldown = !!cd && !isNaN(cd.getTime()) && cd.getTime() > Date.now();
  if (m.status === 'Active' && !cancelPending) {
    // A member can hold several memberships (Open Gym + Pickleball + …) — offer Add before Change.
    const addBtn = h('button', { class: 'e365-slot', type: 'button' }, 'Add membership') as HTMLButtonElement;
    addBtn.addEventListener('click', () => void showPlanPicker(null, mine, cfg, api, auth, root));
    actions.append(addBtn);
    if (!inCooldown) {
      const changeBtn = h('button', { class: 'e365-slot', type: 'button' }, 'Change plan') as HTMLButtonElement;
      changeBtn.addEventListener('click', () => void showPlanPicker(m, mine, cfg, api, auth, root));
      actions.append(changeBtn);
    }
    const cancelBtn = h('button', { class: 'e365-slot', type: 'button' }, 'Cancel membership') as HTMLButtonElement;
    cancelBtn.addEventListener('click', () => void cancelMembership(m, cfg, api, auth, cancelBtn, root));
    actions.append(cancelBtn);
  }
  if (actions.childNodes.length) card.append(actions);
  if (m.status === 'Active' && !cancelPending && inCooldown)
    card.append(h('div', { class: 'e365-muted', style: 'font-size:.78rem; margin-top:6px;' },
      `You can switch plans again after ${fmtDate(m.planChangeAvailableUtc)}.`));

  return card;
}

// Plan picker, two modes sharing one screen:
//   m != null → "Change plan": switching swaps the subscription in place (server prorates onto the
//               next invoice) — same membership + card, no cancel/rejoin. Each row also offers Add.
//   m == null → "Add membership": join ANOTHER plan alongside the ones you have (a member can hold
//               several — e.g. Open Gym + Pickleball). Adding starts the normal first-party join flow.
// Plans already held by any non-cancelled membership are labeled instead of clickable (the server
// rejects double-subscribing the same plan).
async function showPlanPicker(m: MyMembership | null, mine: MyMembership[], cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, m ? 'Change plan' : 'Add membership'));
  const back = h('button', { class: 'e365-slot', type: 'button', style: 'margin-bottom:10px;' }, '← Back') as HTMLButtonElement;
  back.addEventListener('click', () => void renderManage(root, cfg, api, auth));
  root.append(back);

  const host = h('div');
  root.append(host);
  renderLoading(host, 'Plans');

  let plans: MembershipPlan[];
  try {
    plans = await fetchPlans(cfg, api);
  } catch {
    clear(host);
    host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load the plans. Please try again.'));
    const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
    retry.addEventListener('click', () => void showPlanPicker(m, mine, cfg, api, auth, root));
    host.append(retry, footer());
    return;
  }

  clear(host);
  host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 12px; font-size:.85rem;' },
    m ? 'Switch anytime — you keep the same member card, and we’ll prorate the difference onto your next bill. Or add a plan as a second membership.'
      : 'Add another membership alongside the ones you have — each gets its own member card and bills separately.'));

  const options: { plan: MembershipPlan; interval: 'Monthly' | 'Annual'; price: number }[] = [];
  for (const p of plans || []) {
    if (p.priceMonthly != null && p.priceMonthly > 0) options.push({ plan: p, interval: 'Monthly', price: p.priceMonthly });
    if (p.priceAnnual != null && p.priceAnnual > 0) options.push({ plan: p, interval: 'Annual', price: p.priceAnnual });
  }

  // Plans covered by the user's Active/PastDue memberships at this venue (matches the server's
  // duplicate guard — an unfinished Incomplete signup or an ended one doesn't block joining).
  const owned = new Set(mine
    .filter((x) => (x.status === 'Active' || x.status === 'PastDue') && x.membershipPlanId)
    .map((x) => (x.membershipPlanId || '').toLowerCase()));

  for (const o of options) {
    const pid = o.plan.id.toLowerCase();
    const isCurrent = !!m && pid === (m.membershipPlanId || '').toLowerCase() && o.interval === m.interval;
    const sameAsCurrentPlan = !!m && pid === (m!.membershipPlanId || '').toLowerCase();
    const row = h('div', {
      style: 'display:flex; justify-content:space-between; align-items:center; gap:10px; border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:10px 12px; margin-bottom:8px; background:var(--e365-surface);',
    });
    const left = h('div');
    left.append(h('div', { style: 'font-weight:700;' }, o.plan.name));
    const pct = taxPct(cfg, o.plan);
    left.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem;' }, listPrice(o.price, o.interval, pct)));
    const bd = taxBreakdown(o.price, o.interval, pct);
    if (bd) left.append(h('div', { class: 'e365-muted', style: 'font-size:.74rem;' }, bd));
    row.append(left);
    if (isCurrent) {
      row.append(h('span', { class: 'e365-muted', style: 'font-size:.78rem; font-weight:700;' }, 'Current plan'));
    } else if (sameAsCurrentPlan) {
      // Same plan, other interval — a switch (billing change), never an add (same-plan double-subscribe).
      const sw = h('button', { class: 'e365-slot', type: 'button' }, 'Switch') as HTMLButtonElement;
      sw.addEventListener('click', () => void doChangePlan(m!, o.plan, o.interval, cfg, api, auth, root, sw));
      row.append(sw);
    } else if (owned.has(pid)) {
      row.append(h('span', { class: 'e365-muted', style: 'font-size:.78rem; font-weight:700;' }, 'You have this plan'));
    } else {
      const btns = h('div', { style: 'display:flex; gap:6px; flex-shrink:0;' });
      if (m) {
        const sw = h('button', { class: 'e365-slot', type: 'button' }, 'Switch') as HTMLButtonElement;
        sw.addEventListener('click', () => void doChangePlan(m!, o.plan, o.interval, cfg, api, auth, root, sw));
        btns.append(sw);
      }
      const add = h('button', { class: m ? 'e365-slot' : 'e365-btn', type: 'button' }, 'Add') as HTMLButtonElement;
      add.addEventListener('click', () => startJoin(cfg, o.plan, o.interval, add));
      btns.append(add);
      row.append(btns);
    }
    host.append(row);
  }

  host.append(footer());
}

async function doChangePlan(m: MyMembership, plan: MembershipPlan, interval: 'Monthly' | 'Annual', cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement, btn: HTMLButtonElement): Promise<void> {
  if (!window.confirm(`Switch to ${plan.name} (${interval.toLowerCase()})? You’ll keep your member card, and the price difference is prorated onto your next bill.`)) return;
  btn.disabled = true;
  btn.textContent = 'Switching…';
  try {
    await api.post(`/api/memberships/${m.id}/change-plan`, { targetPlanId: plan.id, interval });
    void renderManage(root, cfg, api, auth); // back to the card, now on the new plan
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Switch';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-membership' }); return; }
    window.alert('We couldn’t change your plan right now. Please try again.');
  }
}

async function openBillingPortal(m: MyMembership, api: Api, btn: HTMLButtonElement, auth: Auth): Promise<void> {
  const original = btn.textContent || 'Update card';
  btn.disabled = true;
  btn.textContent = 'Opening…';
  try {
    const res = await api.post<{ portalUrl?: string }>(`/api/memberships/${m.id}/billing-portal`,
      { returnUrl: location.href.split('?')[0] });
    if (res && res.portalUrl) { window.location.href = res.portalUrl; return; }
    throw new Error('no portal url');
  } catch (e) {
    btn.disabled = false;
    btn.textContent = original;
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-membership' }); return; }
    window.alert('We couldn’t open the payment update page. Please try again.');
  }
}

async function cancelMembership(m: MyMembership, cfg: EmbedConfig, api: Api, auth: Auth, btn: HTMLButtonElement, root: HTMLElement): Promise<void> {
  const end = fmtDate(m.currentPeriodEndUtc);
  const msg = end
    ? `Cancel your ${m.planName || 'membership'}? You’ll keep access until ${end}, then it won’t renew.`
    : `Cancel your ${m.planName || 'membership'}? It won’t renew.`;
  if (!window.confirm(msg)) return;
  btn.disabled = true;
  btn.textContent = 'Cancelling…';
  try {
    await api.post(`/api/memberships/${m.id}/cancel`);
    void renderManage(root, cfg, api, auth);
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Cancel membership';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-membership' }); return; }
    window.alert('We couldn’t cancel right now. Please try again.');
  }
}
